import React, { useEffect, useId, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { MutateSetTargetPitSoc } from "../../../utils/graphql/hooks/raceDataHooks";
import styles from "./style.module.scss";

export default function PitBoostSocControls({
  carColour,
}: {
  carColour: string;
}) {
  const raceConfig = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig,
  );
  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
  );
  const minSoc = Math.floor(
    strategyImport?.rBMSSoCMinMinPitWindowPrediction ?? 40,
  );
  const maxSoc = Math.ceil(
    strategyImport?.rBMSSoCMinMaxPitWindowPrediction ?? 60,
  );
  const confirmedTarget = raceConfig?.targetPitSoc;
  const [draft, setDraft] = useState(() => confirmedTarget?.toString() ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const setTargetPitSoc = MutateSetTargetPitSoc();

  useEffect(() => {
    setDraft(confirmedTarget?.toString() ?? "");
    setError(null);
  }, [confirmedTarget]);

  const targetPitSoc = draft.trim() === "" ? NaN : Number(draft);
  const valid =
    Number.isFinite(targetPitSoc) &&
    targetPitSoc >= minSoc &&
    targetPitSoc <= maxSoc;
  const disabled = !raceConfig || raceConfig.pitLap !== null || saving;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (disabled || !valid || targetPitSoc === confirmedTarget) return;

    setError(null);
    setSaving(true);
    try {
      const result = await setTargetPitSoc({
        variables: { carColour, targetPitSoc },
      });
      if (result.errors?.length) {
        throw new Error(result.errors.map((item) => item.message).join(" "));
      }
      if (result.data?.setTargetPitSoc === false) {
        throw new Error("Could not update the target pit boost SoC.");
      }
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Could not update the target pit boost SoC.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit}
      aria-busy={saving}
      noValidate
    >
      <label className={styles.label} htmlFor={inputId}>
        Target Pit Boost SoC
      </label>
      <div className={styles.controls}>
        <input
          id={inputId}
          className={styles.input}
          aria-label="Target Pit Boost SoC (%)"
          type="number"
          min={minSoc}
          max={maxSoc}
          step="1"
          required
          value={draft}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
          }}
          onBlur={() => {
            if (!valid) setDraft(confirmedTarget?.toString() ?? String(minSoc));
          }}
        />
        <button
          className={styles.applyButton}
          type="submit"
          disabled={disabled || !valid || targetPitSoc === confirmedTarget}
        >
          {saving ? "Saving…" : "Apply"}
        </button>
      </div>
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
